import { privateHttp, publicHttp } from "~/config/axios";

/**
 * 약관 조회 (비로그인 가능).
 * 백엔드: /api/common/terms/** — 가입 전 이용자·탈퇴 회원도 열람할 수 있어야 해서 인증을 걸지 않는다.
 */

export type TermsTargetRole = "COMMON" | "GUARDIAN" | "BUSINESS";

export type TermsType =
  | "TERMS_TYPE_SERVICE"
  | "TERMS_TYPE_PRIVACY"
  | "TERMS_TYPE_LOCATION"
  | "TERMS_TYPE_MARKETING";

export type TermsStatus = "DRAFT" | "SCHEDULED" | "ACTIVE" | "ARCHIVED";

export interface TermsDocument {
  id: number;
  targetRole: TermsTargetRole;
  termsType: TermsType;
  termsTitle: string;
  termsVersion: string;
  /** 목록·이력 응답에서는 null */
  contentHtml?: string | null;
  termsUrl?: string | null;
  changeSummary?: string | null;
  isRequired: boolean;
  status: TermsStatus;
  effectiveAt?: string | null;
  noticeAt?: string | null;
}

interface ApiResponse<T> {
  data: T;
}

export const TERMS_TYPE_LABEL: Record<TermsType, string> = {
  TERMS_TYPE_SERVICE: "이용약관",
  TERMS_TYPE_PRIVACY: "개인정보처리방침",
  TERMS_TYPE_LOCATION: "위치기반서비스",
  TERMS_TYPE_MARKETING: "마케팅 수신동의",
};

export const TERMS_STATUS_LABEL: Record<TermsStatus, string> = {
  DRAFT: "임시저장",
  SCHEDULED: "시행 예정",
  ACTIVE: "현행",
  ARCHIVED: "이전 버전",
};

/** 대상별 현행 약관 목록 (유형당 1건, 본문 제외) */
export const getTermsDocuments = async (
  targetRole: TermsTargetRole,
): Promise<TermsDocument[]> => {
  const { data } = await publicHttp.get<ApiResponse<TermsDocument[]>>(
    "/common/terms/documents",
    { params: { targetRole } },
  );
  return data.data ?? [];
};

/** 특정 버전 본문 */
export const getTermsVersion = async (
  infoTermsSeq: number,
): Promise<TermsDocument> => {
  const { data } = await publicHttp.get<ApiResponse<TermsDocument>>(
    `/common/terms/${infoTermsSeq}`,
  );
  return data.data;
};

/** 현행 약관 본문 (가입 화면 등에서 유형만 알고 있을 때) */
export const getEffectiveTerms = async (
  targetRole: TermsTargetRole,
  termsType: TermsType,
): Promise<TermsDocument | null> => {
  try {
    const { data } = await publicHttp.get<ApiResponse<TermsDocument>>(
      "/common/terms/effective",
      { params: { targetRole, termsType } },
    );
    return data.data ?? null;
  } catch {
    // 아직 DB 에 등록되지 않은 유형 — 호출부에서 레거시 하드코딩 본문으로 폴백한다
    return null;
  }
};

/** 버전 이력 (시행 예정 포함, 임시저장 제외) */
export const getTermsHistory = async (
  targetRole: TermsTargetRole,
  termsType: TermsType,
): Promise<TermsDocument[]> => {
  const { data } = await publicHttp.get<ApiResponse<TermsDocument[]>>(
    "/common/terms/history",
    { params: { targetRole, termsType } },
  );
  return data.data ?? [];
};

/** 고지 중인 개정 약관 (시행 예정 + 고지 시작일 도래) — 홈 배너용 */
export const getTermsNotices = async (
  targetRole: TermsTargetRole,
): Promise<TermsDocument[]> => {
  const { data } = await publicHttp.get<ApiResponse<TermsDocument[]>>(
    "/common/terms/notices",
    { params: { targetRole } },
  );
  return data.data ?? [];
};

/** 앱 빌드 종류에 따른 재동의 API 경로 (보호자/사업자는 동의 로그 테이블이 다르다) */
const consentBase = () =>
  import.meta.env.VITE_APP_TYPE === "business" ? "/business/terms" : "/user/terms";

/** 재동의 대상 — 현행 필수 약관 중 내 동의 기록이 없는 것 */
export const getPendingTerms = async (): Promise<TermsDocument[]> => {
  const { data } = await privateHttp.get<ApiResponse<TermsDocument[]>>(
    `${consentBase()}/pending`,
  );
  return data.data ?? [];
};

/** 약관 동의 */
export const agreeTerms = async (infoTermsSeqs: number[]): Promise<void> => {
  await privateHttp.post(`${consentBase()}/agree`, { infoTermsSeqs });
};
